import React, { useState, useEffect, useRef, useCallback } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { getFeaturedListingsProps } from '../../util/data';

import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// ---------------------------------------------------------------------------
// Host Earnings Estimator Modal
// ---------------------------------------------------------------------------

const RATE_LOW = 300;
const RATE_HIGH = 800;
const NIGHTS_LOW = 7;
const NIGHTS_HIGH = 10;
const MIN_PETS = 1;
const MAX_PETS = 4;

const CITIES = [
  'Mumbai (incl. Thane & Navi Mumbai)',
  'Pune',
  'Bangalore',
  'Hyderabad',
  'Chennai',
  'Delhi NCR',
  'Gurugram',
  'Other city',
];

const token = {
  bgPage: '#F1ECF9',
  card: '#FFFDFB',
  ink: '#2A2140',
  inkSoft: '#6B6180',
  marigold: '#E8873D',
  marigoldDeep: '#C4681F',
  indigo: '#6D3EA6',
  rose: '#9B3E8A',
  money: '#5B3A9E',
  line: 'rgba(42,33,64,0.10)',
  radius: '22px',
};

const PAW_SVG_PATH =
  'M8.5 9.5c1.1 0 2-1.12 2-2.5S9.6 4.5 8.5 4.5 6.5 5.62 6.5 7s.9 2.5 2 2.5zm7 0c1.1 0 2-1.12 2-2.5S16.6 4.5 15.5 4.5s-2 1.12-2 2.5.9 2.5 2 2.5zM4.5 13c1.1 0 2-1.12 2-2.5S5.6 8 4.5 8 2.5 9.12 2.5 10.5 3.4 13 4.5 13zm15 0c1.1 0 2-1.12 2-2.5S20.6 8 19.5 8s-2 1.12-2 2.5.9 2.5 2 2.5zM12 12c-2.7 0-8 1.4-8 4.6 0 1.2.9 2.4 2.3 2.4.9 0 1.6-.5 2.5-1 .9-.5 1.9-1.1 3.2-1.1s2.3.6 3.2 1.1c.9.5 1.6 1 2.5 1 1.4 0 2.3-1.2 2.3-2.4 0-3.2-5.3-4.6-8-4.6z';

function PawIcon({ lit }) {
  return (
    <svg
      viewBox="0 0 24 24"
      style={{
        width: 14,
        height: 14,
        opacity: lit ? 1 : 0.18,
        transform: lit ? 'scale(1)' : 'scale(0.6)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
      }}
    >
      <path d={PAW_SVG_PATH} fill={lit ? token.indigo : token.inkSoft} />
    </svg>
  );
}

function useAnimatedNumber(target, duration = 450) {
  const [display, setDisplay] = useState(target);
  const prevRef = useRef(target);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = prevRef.current;
    const to = target;
    if (from === to) return;
    const start = performance.now();
    function tick(now) {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        prevRef.current = to;
      }
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return display;
}

function formatINR(n) {
  return '\u20b9' + n.toLocaleString('en-IN');
}

function HostEarningsModal({ onClose }) {
  const [pets, setPets] = useState(2);
  const [city, setCity] = useState(CITIES[0]);
  const [closeBtnHover, setCloseBtnHover] = useState(false);
  const [ctaHover, setCtaHover] = useState(false);

  const low = pets * NIGHTS_LOW * RATE_LOW;
  const high = pets * NIGHTS_HIGH * RATE_HIGH;

  const displayLow = useAnimatedNumber(low);
  const displayHigh = useAnimatedNumber(high);

  useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const litCount = NIGHTS_LOW + pets - 2;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400..800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500;600&display=swap');
        @keyframes purfurry-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes purfurry-rise-in { from { opacity: 0; transform: translateY(18px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(20,28,33,0.42)',
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
          zIndex: 9999,
          animation: 'purfurry-fade-in 0.35s ease both',
        }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        role="presentation"
      >
        <div
          style={{
            width: '100%',
            maxWidth: 440,
            background: token.card,
            borderRadius: token.radius,
            border: `1px solid ${token.line}`,
            boxShadow: '0 30px 60px -20px rgba(34,48,58,0.35), 0 2px 8px rgba(34,48,58,0.08)',
            overflow: 'hidden',
            animation: 'purfurry-rise-in 0.4s cubic-bezier(.2,.8,.2,1) both',
            position: 'relative',
            fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
            color: token.ink,
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="purfurry-modal-title"
        >
          {/* Close button */}
          <button
            style={{
              position: 'absolute',
              top: 14,
              right: 14,
              width: 32,
              height: 32,
              borderRadius: '50%',
              border: `1px solid ${token.line}`,
              background: closeBtnHover ? token.rose : 'rgba(255,255,255,0.7)',
              color: closeBtnHover ? '#fff' : token.inkSoft,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 16,
              lineHeight: 1,
              transition: 'background 0.15s, color 0.15s, transform 0.15s',
              transform: closeBtnHover ? 'rotate(90deg)' : 'none',
              zIndex: 5,
            }}
            aria-label="Close"
            onClick={onClose}
            onMouseEnter={() => setCloseBtnHover(true)}
            onMouseLeave={() => setCloseBtnHover(false)}
          >
            ✕
          </button>

          {/* Header */}
          <div
            style={{
              padding: '30px 30px 20px',
              background: 'linear-gradient(180deg, rgba(109,62,166,0.14), rgba(109,62,166,0) 70%)',
              borderBottom: `1px solid ${token.line}`,
            }}
          >
            <div
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: token.marigoldDeep,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 10,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={token.indigo}>
                <path d={PAW_SVG_PATH} />
              </svg>
              Become a Host
            </div>
            <h1
              id="purfurry-modal-title"
              style={{
                fontFamily: "'Bricolage Grotesque', 'Inter', sans-serif",
                fontSize: 26,
                lineHeight: 1.15,
                fontWeight: 700,
                margin: '0 0 8px',
                letterSpacing: '-0.01em',
                color: token.ink,
              }}
            >
              How much could you earn?
            </h1>
          </div>

          {/* Body */}
          <div style={{ padding: '24px 30px 30px' }}>
            {/* City selector */}
            <div style={{ marginBottom: 18 }}>
              <label
                htmlFor="purfurry-city-select"
                style={{
                  display: 'block',
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: token.ink,
                  marginBottom: 8,
                }}
              >
                Your city
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  id="purfurry-city-select"
                  style={{
                    width: '100%',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 15,
                    fontWeight: 500,
                    color: token.ink,
                    background: '#fff',
                    border: `1.5px solid ${token.line}`,
                    borderRadius: 12,
                    padding: '12px 40px 12px 14px',
                    cursor: 'pointer',
                  }}
                  value={city}
                  onChange={e => setCity(e.target.value)}
                >
                  {CITIES.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    right: 14,
                    top: '50%',
                    width: 9,
                    height: 9,
                    borderRight: `2px solid ${token.inkSoft}`,
                    borderBottom: `2px solid ${token.inkSoft}`,
                    transform: 'translateY(-65%) rotate(45deg)',
                    pointerEvents: 'none',
                    display: 'block',
                  }}
                />
              </div>
            </div>

            {/* Pet stepper */}
            <div style={{ marginBottom: 18 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: token.ink,
                  marginBottom: 8,
                }}
              >
                Pets you can host at once
              </label>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: '#fff',
                  border: `1.5px solid ${token.line}`,
                  borderRadius: 12,
                  padding: 6,
                }}
              >
                <button
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 9,
                    border: 'none',
                    background: token.bgPage,
                    color: token.indigo,
                    fontSize: 18,
                    fontWeight: 700,
                    cursor: pets <= MIN_PETS ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: pets <= MIN_PETS ? 0.35 : 1,
                    transition: 'background 0.15s',
                  }}
                  disabled={pets <= MIN_PETS}
                  aria-label="Decrease pets"
                  onClick={() => setPets(p => Math.max(MIN_PETS, p - 1))}
                >
                  −
                </button>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    lineHeight: 1.1,
                    gap: 2,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontSize: 20,
                      fontWeight: 700,
                    }}
                  >
                    {pets}
                  </span>
                  <span
                    style={{
                      fontSize: 10.5,
                      color: token.inkSoft,
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}
                  >
                    {pets === 1 ? 'pet' : 'pets'}
                  </span>
                </div>
                <button
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 9,
                    border: 'none',
                    background: token.bgPage,
                    color: token.indigo,
                    fontSize: 18,
                    fontWeight: 700,
                    cursor: pets >= MAX_PETS ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: pets >= MAX_PETS ? 0.35 : 1,
                    transition: 'background 0.15s',
                  }}
                  disabled={pets >= MAX_PETS}
                  aria-label="Increase pets"
                  onClick={() => setPets(p => Math.min(MAX_PETS, p + 1))}
                >
                  +
                </button>
              </div>

              {/* Paw trail */}
              <div
                aria-hidden="true"
                style={{
                  display: 'flex',
                  gap: 4,
                  justifyContent: 'center',
                  marginTop: 14,
                  height: 16,
                }}
              >
                {Array.from({ length: NIGHTS_HIGH }).map((_, i) => (
                  <PawIcon key={i} lit={i < litCount} />
                ))}
              </div>
            </div>

            {/* Result card */}
            <div
              style={{
                marginTop: 22,
                background: 'linear-gradient(165deg, #F5F0FB, #EFE6FA)',
                border: '1px solid rgba(91,58,158,0.18)',
                borderRadius: 16,
                padding: '20px 20px 18px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: token.money,
                  marginBottom: 6,
                }}
              >
                Estimated monthly earnings
              </div>
              <div
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 27,
                  fontWeight: 600,
                  color: token.ink,
                  letterSpacing: '-0.01em',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatINR(displayLow)}
                <span style={{ color: token.inkSoft, fontWeight: 400, margin: '0 4px' }}>–</span>
                {formatINR(displayHigh)}
              </div>
              <div style={{ fontSize: 12, color: token.inkSoft, marginTop: 2 }}>in {city}</div>
              <div
                style={{
                  fontSize: 11.5,
                  color: token.inkSoft,
                  marginTop: 12,
                  lineHeight: 1.5,
                  borderTop: '1px dashed rgba(34,48,58,0.15)',
                  paddingTop: 10,
                }}
              >
                Based on{' '}
                <strong style={{ color: token.ink, fontWeight: 600 }}>
                  {pets} {pets === 1 ? 'pet' : 'pets'}
                </strong>{' '}
                · <strong style={{ color: token.ink, fontWeight: 600 }}>7–10 nights</strong> hosted
                a month · <strong style={{ color: token.ink, fontWeight: 600 }}>₹300–800</strong>{' '}
                per pet, per night
              </div>
            </div>

            {/* CTA */}
            <button
              id="purfurry-become-host-cta"
              style={{
                width: '100%',
                marginTop: 20,
                background: ctaHover ? token.marigoldDeep : token.marigold,
                color: ctaHover ? '#fff' : '#2A1B04',
                border: 'none',
                borderRadius: 12,
                padding: 14,
                fontFamily: "'Inter', sans-serif",
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                transition: 'background 0.15s, transform 0.1s',
              }}
              onMouseEnter={() => setCtaHover(true)}
              onMouseLeave={() => setCtaHover(false)}
              onClick={() => {
                window.location.href = 'https://www.purfurry.com/signup/provider';
              }}
            >
              Become a host
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  width: 16,
                  height: 16,
                  transition: 'transform 0.15s',
                  transform: ctaHover ? 'translateX(3px)' : 'none',
                }}
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>

            <p
              style={{
                fontSize: 10.5,
                color: token.inkSoft,
                textAlign: 'center',
                marginTop: 12,
                opacity: 0.75,
              }}
            >
              Estimate only. Actual earnings depend on demand, listing quality and availability.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// CMSPage
// ---------------------------------------------------------------------------

export const CMSPageComponent = props => {
  const { params, pageAssetsData, inProgress, error } = props;
  const pageId = params.pageId || props.pageId;

  const [showModal, setShowModal] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (pageId === 'become_a_host') {
      // Random delay between 15 000 ms and 20 000 ms
      const delay = 15000 + Math.random() * 5000;
      timerRef.current = setTimeout(() => setShowModal(true), delay);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [pageId]);

  const handleClose = useCallback(() => setShowModal(false), []);

  if (!inProgress && error?.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  return (
    <>
      <PageBuilder
        pageAssetsData={pageAssetsData?.[pageId]?.data}
        inProgress={inProgress}
        schemaType="Article"
        featuredListings={getFeaturedListingsProps(pageId, props)}
      />
      {showModal && <HostEarningsModal onClose={handleClose} />}
    </>
  );
};

CMSPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};

  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);

  return { pageAssetsData, featuredListingData, getListingEntitiesById, inProgress, error };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const CMSPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(CMSPageComponent);

export default CMSPage;
